// Single source of truth for turning a persisted activity (from the
// Library, from a Session, or from a local draft) into the per-tab
// workspace shape used by TrainingGround.
//
// Consolidates the three load paths that currently diverge
// (see DATA_INTEGRITY_INVESTIGATION.md, Issue 3):
//   A  handleLoadActivity        (Library row → current tab)
//   B  handleLoadSession         (Session → all tabs)
//   C  handleAddActivityToSession (Library "+" → new tab)
//
// The logic below is extracted from the existing handleLoadActivity
// (path A), which is the only path that currently applies the legacy
// half-pitch migration, plus `showDimensions` handling (default true
// when the field is missing, per product decision).
//
// Pure: no React, no DOM, no network. Not yet wired into the app.
import {
  LEGACY_PITCH_MODE_ALIAS,
  DEFAULT_FIELD_COLOR,
  normalizePlayerSize,
  isPlayerLikeType,
} from "../constants/pitchObjects";

export const LEGACY_HALF_SHIFT_Y = 525;
export const CURRENT_CANONICAL_VERSION = 2;

// Legacy half-pitch records (saved before the canonical-coordinate
// foundation) stored y relative to the half pitch (0 = halfway line).
export const needsLegacyHalfMigration = (a) => {
  const rawMode = (a && a.pitchMode) || "vertical-full";
  return /-half$/.test(rawMode) && (!a.canonicalVersion || a.canonicalVersion < CURRENT_CANONICAL_VERSION);
};

const shiftPoint = (o, dy) => ({ ...o, y: (o.y || 0) + dy });
const shiftEndpoints = (it, dy) => ({
  ...it,
  y1: (it.y1 || 0) + dy,
  y2: (it.y2 || 0) + dy,
  ...(typeof it.cy === "number" ? { cy: it.cy + dy } : {}),
});
const shiftShape = (s, dy) => {
  if (s.type === "rect") return { ...s, y: (s.y || 0) + dy };
  if (s.type === "circle") return { ...s, cy: (s.cy || 0) + dy };
  return s;
};

const nonEmpty = (v) => v != null && v !== "";

// Returns the per-tab shape (without tabId / activityId, which each
// caller owns):
//   { activity: {name, rules, time, sets, rest, coachingPoints, rotations},
//     pitchMode, fieldColor, showDimensions, board: {objects, arrows, shapes, texts, lines} }
export function normalizeLoadedActivity(a) {
  const src = a || {};
  const rawMode = src.pitchMode || "vertical-full";
  const pitchMode = LEGACY_PITCH_MODE_ALIAS[rawMode] || rawMode;
  const migrate = needsLegacyHalfMigration(src);
  const dy = migrate ? LEGACY_HALF_SHIFT_Y : 0;

  const objects = (src.objects || [])
    .map((o) => ({
      ...o,
      size: isPlayerLikeType(o.type) ? o.size : (typeof o.size === "number" ? o.size : 1),
    }))
    .map(normalizePlayerSize)
    .map((o) => (migrate ? shiftPoint(o, dy) : o));

  const board = {
    objects,
    arrows: (src.arrows || []).map((it) => (migrate ? shiftEndpoints(it, dy) : it)),
    shapes: (src.shapes || []).map((s) => (migrate ? shiftShape(s, dy) : s)),
    texts: (src.texts || []).map((t) => (migrate ? shiftPoint(t, dy) : t)),
    lines: (src.lines || []).map((it) => (migrate ? shiftEndpoints(it, dy) : it)),
  };

  const activity = {
    name: src.name || "",
    // Legacy `description` → `rules`.
    rules: nonEmpty(src.rules) ? src.rules : (src.description || ""),
    // Legacy single `duration` → time with sets = 1.
    time: nonEmpty(src.time) ? src.time : (src.duration || ""),
    sets: nonEmpty(src.sets) ? src.sets : (src.duration ? "1" : ""),
    rest: nonEmpty(src.rest) ? src.rest : "",
    coachingPoints: Array.isArray(src.coachingPoints) ? src.coachingPoints : [],
    rotations: Array.isArray(src.rotations) ? src.rotations : [],
  };

  return {
    activity,
    pitchMode,
    fieldColor: src.fieldColor || DEFAULT_FIELD_COLOR,
    // Product decision: default true when missing.
    showDimensions: src.showDimensions != null ? !!src.showDimensions : true,
    board,
  };
}

// Deep-ish clone with fresh ids for every canvas entity, coaching point
// and rotation — the independence guarantee used by "Add to session"
// and "Duplicate activity".
export function withFreshIds(normalized, newId) {
  const fresh = (o) => ({ ...o, id: newId() });
  const b = normalized.board;
  return {
    ...normalized,
    activity: {
      ...normalized.activity,
      coachingPoints: normalized.activity.coachingPoints.map(fresh),
      rotations: normalized.activity.rotations.map(fresh),
    },
    board: {
      objects: b.objects.map(fresh),
      arrows: b.arrows.map(fresh),
      shapes: b.shapes.map(fresh),
      texts: b.texts.map(fresh),
      lines: b.lines.map(fresh),
    },
  };
}
